import type { Product, SuggestionSurface } from "./pdp-types";

/** The query param that names the surface; the design prototype's own (`logic.js`, `de=`). */
export const SUGGESTION_SURFACE_PARAM = "de";

/**
 * A piece's own address — the one place that knows which of the two PDP routes a piece lives at.
 *
 * Two routes, because a piece has two possible natures:
 *
 * - **on a list** → `/lista/{slug}/produto/{itemId}`, the page a guest opens. Addressed by the
 *   list item, which is also what a guest buys.
 * - **on no list** → `/produtos/{productId}`, the catalog's own page. Reached by a host deciding
 *   whether to add the piece, and (once the backend serves public templates) by a visitor who
 *   has no list yet.
 *
 * Centralized because `product-metadata.ts` and `product-structured-data.ts` each used to build
 * this template by hand from `giftListSlug` + `id`. With an empty slug both produced
 * `/lista//produto/{id}` — a canonical URL and a JSON-LD `@id` pointing at a route that does not
 * exist. One function, so the two cannot disagree and neither can be forgotten.
 */
export function pdpCanonicalPath(product: Pick<Product, "id" | "productId" | "giftListSlug">): string {
  if (product.giftListSlug.trim() === "") {
    return productPageHref(product.productId);
  }

  return `/lista/${product.giftListSlug}/produto/${product.id}`;
}

/**
 * The catalog page of one product, plain — no context, no scope.
 *
 * Split out of the function above so that whoever needs only this address does not rebuild the
 * template: that is precisely how `/lista//produto/{id}` got written in three places
 * (`product-metadata`, `product-structured-data`, and later `suggestion-card`).
 */
export function productPageHref(productId: string): string {
  return `/produtos/${encodeURIComponent(productId)}`;
}

/**
 * A piece's page, opened **from a suggestion list**: the catalog page plus the list it came from
 * (`lista`) and, off the public page, the surface (`de`). Sits beside {@link productPageHref}
 * so the template is not rebuilt by hand in each list that opens pieces.
 *
 * No `contexto`: without it the route already lands in `sugestao`. The public surface is the
 * absence of `de`, which keeps the address the page has always handed out as short as possible.
 */
export function suggestionPieceHref(productId: string, slug: string, surface: SuggestionSurface): string {
  const params = new URLSearchParams({ lista: slug });
  if (surface !== "publica") params.set(SUGGESTION_SURFACE_PARAM, surface);

  return `${productPageHref(productId)}?${params.toString()}`;
}
